/** @jsxImportSource @emotion/react */

import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';

import { moneyFormatter } from '@utils/customLib';
import { slabsList } from '@utils/cred';

const feeFormat = {
	minimumFractionDigits: 4,
};

const Row = ({
	n,
	v30: { start, end },
	// ao,
	bnsh: { min, max },
	bnsd,
	// index,
}) => (
	<tr className="border-b border-gray-200">
		<td className="py-8 px-4 md:px-8 text-center sticky left-0 bg-white">
			{n}
		</td>
		<td className="py-8 px-4 md:px-8 text-right">
			{start || (start === 0 && !end)
				? `≥ ${moneyFormatter(start, undefined)} USDT`
				: null}{' '}
			{start && end ? '& ' : null}
			{end ? `< ${moneyFormatter(end, undefined)} USDT` : null}
		</td>
		<td className="py-8 px-4 md:px-8 text-right">
			{min ? `≥ $${moneyFormatter(min, undefined)}` : null}
			{min && max ? '& ' : null}
			{max ? `< $${moneyFormatter(max, undefined)}` : null}
		</td>
		<td className="py-8 px-4 md:px-8 text-right">
			{moneyFormatter(bnsd, feeFormat)}%
		</td>
		<td className="py-8 px-4 md:px-8 text-right">0.25%</td>
	</tr>
);

const Fees = ({ slabContext = [] }) => {
	const [slabs, setSlabs] = useState(slabContext);

	useEffect(() => {
		slabsList().then(({ status, data }) => {
			if (status === 1) {
				setSlabs(data);
			}
		});
	}, []);

	return (
		<div>
			<section>
				<h2 className="text-xl font-bold my-4">Spot Trading Fees</h2>
				<p className="u-para--readable mb-6">
					Traders are categorized in VIP levels based on their trading
					volume in the last 30 days and holdings of BNS Token. Higher
					the VIP level, lower the trading fee.
				</p>
				<p className="u-para--readable mb-6">
					<b>NOTE:</b> Fee discounts are applicable only if the{' '}
					<b>Pay with BNS</b> option is enabled. You can do it in the
					Profile section.{' '}
					<a
						href="https://bitbns.com/trade/#/profile/fees"
						className="c-links c-links--iconTri tri-right font-bold"
					>
						profile section
					</a>
				</p>
				<div className="c-table__scrollWrapper">
					<table className="c-table c-table--smallFont c-table--fullWidth border-gray-200">
						<thead className="border-b border-gray-200 text-gray-800 text-xs xs:text-sm">
							<tr>
								<th className="py-8 px-4 md:px-8 align-bottom font-normal text-center sticky left-0 bg-white">
									Level
								</th>
								<th className="py-8 px-4 md:px-8 align-bottom font-normal text-right">
									30d Trade Volume
								</th>
								<th className="py-8 px-4 md:px-8 align-bottom font-normal text-right">
									BNS Balance
								</th>
								<th
									className="py-8 px-4 md:px-8 align-bottom font-normal text-right whitespace-normal w-40"
									width="100"
									css={css`
										min-width: 100px;
										padding-left: 10px !important;
										padding-right: 10px !important;
									`}
								>
									Fees{' '}
									<span className="text-xs">
										(Pay With BNS ON)
									</span>
								</th>
								<th
									className="py-8 px-4 md:px-8 align-bottom font-normal text-right whitespace-normal w-40"
									width="100"
									css={css`
										min-width: 100px;
										padding-left: 10px !important;
										padding-right: 10px !important;
									`}
								>
									Fees{' '}
									<span className="text-xs">
										(Pay With BNS OFF)
									</span>
								</th>
							</tr>
						</thead>
						<tbody>
							{slabs.map((el, index) => (
								<Row {...el} index={index + 1} key={el.l} />
							))}
						</tbody>
					</table>
				</div>
				<div className="text-sm">
					<p className="mt-8">
						<b>Note:</b>
					</p>
					<div className="pl-8">
						<ul className="list-disc my-4">
							<li className="pb-4">
								Minimum fee: ₹0.01 or 0.01 USDT
							</li>
							<li className="pb-4">
								A trader holding less than 25 USDT worth of BNS
								tokens, or not having{' '}
								<b>Pay with BNS enabled</b>, would incur the
								usual 0.25% trade fees.
							</li>
							<li className="pb-4">
								All daily trades will be counted for the
								previous 30 days and the discounts would be
								given through fee refunds everyday at 12 PM IST.
							</li>
						</ul>
						<p>
							Eg: So from March 6th to April 4th, the last 30 days
							trades would be taken to compute the fee refund for
							4th. Trades across all markets (INR, USDT or others)
							will be considered.
						</p>
					</div>
				</div>
			</section>
			<section className="my-16">
				<h2 className="text-xl font-bold my-4">Derivatives Fees</h2>
				<h2 className="text-md font-bold my-4">Futures</h2>
				<p className="u-para--readable mb-6">
					Fees are applicable for both Makers and Takers
				</p>
				<ul className="list-disc pl-8 pb-6">
					<li className="pb-2">
						Maker: <s>0.1%</s> 0% (Introductory Offer)
					</li>
					<li className="pb-2">
						Taker: <b>0.1%</b>
					</li>
				</ul>
				<h2 className="text-md font-bold my-4">Options</h2>
				<p className="u-para--readable mb-6">Coming Soon</p>
			</section>
			<section className="my-16">
				<h2 className="text-xl font-bold my-4">Margin Trading Fees</h2>
				<p className="u-para--readable mb-6">
					Fees are applicable only for lenders. Borrowers are not
					charged any fee
				</p>
				<ul className="list-disc pl-8">
					<li className="pb-2">Borrower: 0%</li>
					<li className="pb-2">
						Lender: <b>15%</b>
					</li>
				</ul>
				<p className="u-para--readable mb-6">
					However, with Pay with BNS, one can avail discount on the
					fee. Here are the slabs:
				</p>
				<div className="c-table__scrollWrapper mb-4">
					<table className="c-table border-gray-200 text-left">
						<thead>
							<tr className="border-b border-gray-200">
								<th className="py-8 px-4 md:px-8 align-top font-normal text-gray-800 text-sm text-right">
									BNS Holding Worth
								</th>
								<th className="py-8 px-4 md:px-8 align-top font-normal text-gray-800 text-sm">
									Discount with <b>Pay with BNS</b> on <br />
									Margin Lending Interest
								</th>
							</tr>
						</thead>
						<tbody>
							<tr className="border-b border-gray-200">
								<td className="py-8 px-4 md:px-8 text-right">
									&gt;200 USD
								</td>
								<td className="py-8 px-4 md:px-8">10%</td>
							</tr>
							<tr className="border-b border-gray-200">
								<td className="py-8 px-4 md:px-8 text-right">
									&gt;500 USD
								</td>
								<td className="py-8 px-4 md:px-8">20%</td>
							</tr>
							<tr className="border-b border-gray-200">
								<td className="py-8 px-4 md:px-8 text-right">
									&gt;1000 USD
								</td>
								<td className="py-8 px-4 md:px-8">33.33%</td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
};

export default Fees;
